import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, FileSpreadsheet, Trash2, Database, Calendar, TrendingUp, TrendingDown, RefreshCw, AlertCircle, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const VTYPE_LABEL = {
  sale: 'Sales', purchase: 'Purchases', receipt: 'Receipts',
  payment: 'Payments', journal: 'Journal', contra: 'Contra',
  credit_note: 'Credit Notes', debit_note: 'Debit Notes', other: 'Other',
};
const VTYPE_COLOR = {
  sale: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  purchase: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  receipt: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  payment: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  journal: 'bg-purple-500/15 text-purple-300 border-purple-500/30',
  contra: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30',
  credit_note: 'bg-teal-500/15 text-teal-300 border-teal-500/30',
  debit_note: 'bg-orange-500/15 text-orange-300 border-orange-500/30',
  other: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
};

export default function HistoricalData() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [vouchers, setVouchers] = useState([]);
  const [vouchersTotal, setVouchersTotal] = useState(0);
  const [filter, setFilter] = useState({ fy: '', voucher_type: '', q: '' });
  const fileInputRef = useRef(null);

  const loadStats = async () => {
    try {
      const res = await axios.get(`${API}/historical-data/stats`, { withCredentials: true });
      setStats(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadVouchers = async () => {
    try {
      const params = new URLSearchParams();
      if (filter.fy) params.append('fy', filter.fy);
      if (filter.voucher_type) params.append('voucher_type', filter.voucher_type);
      if (filter.q) params.append('q', filter.q);
      params.append('limit', '100');
      const res = await axios.get(`${API}/historical-data/vouchers?${params}`, { withCredentials: true });
      setVouchers(res.data.rows || []);
      setVouchersTotal(res.data.total || 0);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { loadStats(); loadVouchers(); }, []);
  useEffect(() => { loadVouchers(); }, [filter.fy, filter.voucher_type, filter.q]);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please upload a CSV file exported from Tally');
      return;
    }
    setUploading(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('source_file', file.name);
      const res = await axios.post(`${API}/historical-data/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadResult(res.data);
      toast.success(`Imported ${res.data.rows_inserted} rows from ${file.name}`);
      loadStats();
      loadVouchers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const deleteUpload = async (uploadId) => {
    if (!window.confirm('Delete this upload batch and all its rows? Cannot be undone.')) return;
    try {
      await axios.delete(`${API}/historical-data/upload/${uploadId}`, { withCredentials: true });
      toast.success('Upload deleted');
      loadStats(); loadVouchers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  const nukeAll = async () => {
    if (!window.confirm('DELETE ALL historical data? Type "YES" to confirm.')) return;
    const confirm2 = window.prompt('Type DELETE to wipe every historical voucher:');
    if (confirm2 !== 'DELETE') return;
    try {
      await axios.delete(`${API}/historical-data/all?confirm=yes`, { withCredentials: true });
      toast.success('All historical data cleared');
      loadStats(); loadVouchers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  if (loading) return <div className="p-8 text-slate-400">Loading…</div>;

  return (
    <div className="p-6 space-y-6" data-testid="historical-data-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Historical Data</h1>
          <p className="text-sm text-slate-400 mt-1">
            Import up to 5 years of Tally CSV exports. Used by the Financial Reports module.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => { loadStats(); loadVouchers(); }}
            className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-sm flex items-center gap-2 border border-slate-700"
            data-testid="refresh-stats-btn"
          >
            <RefreshCw size={14} /> Refresh
          </button>
          {stats?.total > 0 && (
            <button
              onClick={nukeAll}
              className="px-3 py-2 bg-red-900/40 hover:bg-red-900/60 text-red-200 rounded-lg text-sm flex items-center gap-2 border border-red-800"
              data-testid="nuke-all-btn"
            >
              <Trash2 size={14} /> Clear All
            </button>
          )}
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard icon={Database} label="Total Vouchers" value={stats?.total?.toLocaleString('en-IN') || '0'} accent="amber" />
        <StatCard icon={Calendar} label="Date Range"
          value={stats?.date_range ? `${stats.date_range.from?.slice(0,10)} → ${stats.date_range.to?.slice(0,10)}` : '—'} accent="blue" small />
        <StatCard icon={TrendingUp} label="Total Sales"
          value={fmt(stats?.fy_buckets?.reduce((s, b) => s + (b.sales || 0), 0))} accent="emerald" />
        <StatCard icon={TrendingDown} label="Total Purchases"
          value={fmt(stats?.fy_buckets?.reduce((s, b) => s + (b.purchases || 0), 0))} accent="rose" />
      </div>

      {/* Upload zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
        className={`rounded-2xl border-2 border-dashed p-12 text-center transition-all ${
          dragOver ? 'border-amber-400 bg-amber-500/5' : 'border-slate-700 bg-slate-900/40 hover:border-slate-600'
        }`}
        data-testid="upload-dropzone"
      >
        <div className="mx-auto w-14 h-14 rounded-full bg-amber-500/10 flex items-center justify-center mb-4">
          <Upload size={24} className="text-amber-400" />
        </div>
        <p className="text-white font-medium">Drop your Tally CSV export here</p>
        <p className="text-sm text-slate-400 mt-1">or click to browse</p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="file-input"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="mt-4 px-5 py-2 bg-amber-600 hover:bg-amber-500 disabled:bg-slate-700 text-white rounded-lg text-sm flex items-center gap-2 mx-auto"
          data-testid="choose-file-btn"
        >
          <FileSpreadsheet size={14} />
          {uploading ? 'Parsing…' : 'Choose CSV File'}
        </button>
        <p className="text-xs text-slate-500 mt-4">
          Expected columns: Date, Particulars, Vch Type, Vch No., Debit, Credit, Narration
        </p>
      </div>

      {/* Upload result flash */}
      {uploadResult && (
        <div className="rounded-xl p-4 border border-emerald-700 bg-emerald-900/20 flex items-start gap-3" data-testid="upload-result">
          <CheckCircle2 size={20} className="text-emerald-400 mt-0.5" />
          <div className="flex-1 text-sm">
            <div className="font-medium text-emerald-200">
              Imported {uploadResult.rows_inserted} rows — {uploadResult.rows_skipped} skipped
            </div>
            <div className="text-slate-300 mt-1 flex flex-wrap gap-1.5">
              {Object.entries(uploadResult.by_type || {}).map(([k, v]) => (
                <span key={k} className={`px-2 py-0.5 rounded text-xs border ${VTYPE_COLOR[k] || VTYPE_COLOR.other}`}>
                  {VTYPE_LABEL[k] || k}: {v}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* FY breakdown */}
      {stats?.fy_buckets?.length > 0 && (
        <div className="rounded-xl border border-slate-800 bg-slate-900/40">
          <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
            <h3 className="text-white font-medium">Financial-Year Breakdown</h3>
            <div className="text-xs text-slate-400">Indian FY: Apr → Mar</div>
          </div>
          <div className="divide-y divide-slate-800">
            {stats.fy_buckets.map((b) => (
              <div key={b.fy} className="px-5 py-3 flex items-center justify-between text-sm">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-amber-300">FY {b.fy}</span>
                  <span className="text-slate-400 text-xs">{b.count.toLocaleString('en-IN')} vouchers</span>
                </div>
                <div className="flex items-center gap-4 text-xs">
                  <span className="text-emerald-300">Sales {fmt(b.sales)}</span>
                  <span className="text-rose-300">Purchases {fmt(b.purchases)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Uploads history */}
      {stats?.uploads?.length > 0 && (
        <div className="rounded-xl border border-slate-800 bg-slate-900/40">
          <div className="px-5 py-3 border-b border-slate-800">
            <h3 className="text-white font-medium">Upload History</h3>
          </div>
          <div className="divide-y divide-slate-800 text-sm">
            {stats.uploads.map((u) => (
              <div key={u.id} className="px-5 py-3 flex items-center justify-between">
                <div>
                  <div className="text-slate-200">{u.source_file}</div>
                  <div className="text-xs text-slate-500">
                    {new Date(u.uploaded_at).toLocaleString('en-IN')} · by {u.uploaded_by} · {u.rows_inserted} rows
                  </div>
                </div>
                <button
                  onClick={() => deleteUpload(u.id)}
                  className="text-rose-400 hover:text-rose-300 p-2"
                  title="Delete this upload"
                  data-testid={`delete-upload-${u.id}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Voucher viewer */}
      {stats?.total > 0 && (
        <div className="rounded-xl border border-slate-800 bg-slate-900/40">
          <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between flex-wrap gap-2">
            <h3 className="text-white font-medium">Voucher Explorer <span className="text-xs text-slate-400">({vouchersTotal.toLocaleString('en-IN')} matching)</span></h3>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Search party / voucher no."
                value={filter.q}
                onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))}
                className="px-3 py-1.5 bg-slate-800 border border-slate-700 rounded text-sm text-slate-100 w-56"
                data-testid="voucher-search"
              />
              <select
                value={filter.fy}
                onChange={(e) => setFilter((f) => ({ ...f, fy: e.target.value }))}
                className="px-3 py-1.5 bg-slate-800 border border-slate-700 rounded text-sm text-slate-100"
                data-testid="voucher-fy-filter"
              >
                <option value="">All FYs</option>
                {stats.fy_buckets.map((b) => <option key={b.fy} value={b.fy}>FY {b.fy}</option>)}
              </select>
              <select
                value={filter.voucher_type}
                onChange={(e) => setFilter((f) => ({ ...f, voucher_type: e.target.value }))}
                className="px-3 py-1.5 bg-slate-800 border border-slate-700 rounded text-sm text-slate-100"
                data-testid="voucher-type-filter"
              >
                <option value="">All Types</option>
                {Object.entries(VTYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-400 bg-slate-900/60">
                <tr>
                  <th className="text-left px-4 py-2 whitespace-nowrap">Date</th>
                  <th className="text-left px-4 py-2 whitespace-nowrap">Party</th>
                  <th className="text-left px-4 py-2 whitespace-nowrap">Type</th>
                  <th className="text-left px-4 py-2 whitespace-nowrap">Voucher #</th>
                  <th className="text-right px-4 py-2 whitespace-nowrap">Debit</th>
                  <th className="text-right px-4 py-2 whitespace-nowrap">Credit</th>
                  <th className="text-left px-4 py-2 whitespace-nowrap">FY</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800 text-slate-200">
                {vouchers.map((v) => (
                  <tr key={v.id} className="hover:bg-slate-800/40">
                    <td className="px-4 py-2 whitespace-nowrap">{v.date?.slice(0, 10)}</td>
                    <td className="px-4 py-2">{v.party || '—'}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 rounded text-xs border ${VTYPE_COLOR[v.voucher_type] || VTYPE_COLOR.other}`}>
                        {VTYPE_LABEL[v.voucher_type] || v.voucher_type}
                      </span>
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-slate-300">{v.voucher_no || '—'}</td>
                    <td className="px-4 py-2 text-right font-mono text-rose-300">{v.debit ? fmt(v.debit) : '—'}</td>
                    <td className="px-4 py-2 text-right font-mono text-emerald-300">{v.credit ? fmt(v.credit) : '—'}</td>
                    <td className="px-4 py-2 text-xs text-amber-300">{v.fy}</td>
                  </tr>
                ))}
                {vouchers.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">No vouchers match these filters</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {vouchers.length < vouchersTotal && (
            <div className="px-5 py-3 border-t border-slate-800 text-xs text-slate-500 text-center">
              Showing first {vouchers.length} of {vouchersTotal.toLocaleString('en-IN')} vouchers. Use filters to narrow.
            </div>
          )}
        </div>
      )}

      {/* Empty-state hint */}
      {stats?.total === 0 && (
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-8 text-center">
          <AlertCircle className="mx-auto text-slate-600 mb-3" size={32} />
          <p className="text-slate-300 font-medium">No historical data yet</p>
          <p className="text-sm text-slate-500 mt-1">
            Export your Day Book from Tally (Display → Day Book → Alt+E → CSV) and drop the file above.
          </p>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent, small }) {
  const colors = {
    amber: 'border-amber-500/30 bg-amber-500/5 text-amber-300',
    blue: 'border-blue-500/30 bg-blue-500/5 text-blue-300',
    emerald: 'border-emerald-500/30 bg-emerald-500/5 text-emerald-300',
    rose: 'border-rose-500/30 bg-rose-500/5 text-rose-300',
  };
  return (
    <div className={`rounded-xl border p-4 ${colors[accent] || colors.amber}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider opacity-80">
        <Icon size={14} /> {label}
      </div>
      <div className={`mt-2 font-bold text-white ${small ? 'text-sm' : 'text-2xl'}`}>{value}</div>
    </div>
  );
}
